import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import './PdfStatusSection.css';

interface PdfStatusSectionProps {
    projectId: string;
    token: string;
    refreshTrigger?: number;
}

interface PdfStatus {
    totalRechnungen: number;
    rechnungenWithPdf: number;
    rechnungenWithoutPdf: number;
    allPdfsCreated: boolean;
    rechnungenIds: Array<{ id: number; hasPdf: boolean }>;
}

const PdfStatusSection: React.FC<PdfStatusSectionProps> = ({ projectId, token, refreshTrigger }) => {
    const [pdfStatus, setPdfStatus] = useState<PdfStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [createdCount, setCreatedCount] = useState(0);

    useEffect(() => {
        const fetchPdfStatus = async () => {
            try {
                const response = await fetch(`/api/rechnungen/project/${projectId}/pdf-status`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('Fehler beim Laden des PDF-Status');
                }

                const data: PdfStatus = await response.json();
                setPdfStatus(data);
            } catch (error) {
                console.error('Error fetching PDF status:', error);
                toast.error(error instanceof Error ? error.message : 'Fehler beim Laden des PDF-Status');
            } finally {
                setLoading(false);
            }
        };

        fetchPdfStatus();
    }, [projectId, token, refreshTrigger]);

    const refreshPdfStatus = async () => {
        try {
            const response = await fetch(`/api/rechnungen/project/${projectId}/pdf-status`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const data: PdfStatus = await response.json();
                setPdfStatus(data);
            }
        } catch (error) {
            console.error('Error refreshing PDF status:', error);
        }
    };

    const handleCreateRemainingPdfs = async () => {
        if (!pdfStatus || pdfStatus.rechnungenWithoutPdf === 0) {
            return;
        }

        setCreating(true);
        setCreatedCount(0);

        let successCount = 0;
        let failCount = 0;

        try {
            // Use the new bulk endpoint with Server-Sent Events
            const response = await fetch('/api/rechnungen/bulk-create-pdfs', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    projectId: Number(projectId)
                })
            });

            if (!response.ok) {
                throw new Error('Fehler beim Starten der PDF-Erstellung');
            }

            // Read the SSE stream
            const reader = response.body?.getReader();
            const decoder = new TextDecoder();

            if (!reader) {
                throw new Error('Stream nicht verfügbar');
            }

            while (true) {
                const { done, value } = await reader.read();
                
                if (done) {
                    break;
                }

                // Decode the chunk
                const chunk = decoder.decode(value, { stream: true });
                
                // Split by newlines to handle multiple SSE messages
                const lines = chunk.split('\n');
                
                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.slice(6));
                            
                            // Update counts from server's current state
                            if (data.created !== undefined) {
                                successCount = data.created;
                                setCreatedCount(successCount);
                            }
                            if (data.failed !== undefined) {
                                failCount = data.failed;
                            }
                        } catch (e) {
                            console.error('Error parsing SSE message:', e);
                        }
                    }
                }
            }

            // Show summary toast
            if (successCount === 0 && failCount === 0) {
                toast.info('Keine PDFs zu erstellen');
            } else if (failCount === 0 && successCount > 0) {
                toast.success(`${successCount} PDF(s) erfolgreich erstellt`);
            } else if (successCount === 0 && failCount > 0) {
                toast.error(`Alle ${failCount} PDF(s) konnten nicht erstellt werden`);
            } else if (successCount > 0 && failCount > 0) {
                toast.warning(`${successCount} PDF(s) erfolgreich erstellt, ${failCount} fehlgeschlagen`);
            }

            // Refresh PDF status
            await refreshPdfStatus();
        } catch (error) {
            console.error('Error creating PDFs:', error);
            toast.error(error instanceof Error ? error.message : 'Fehler beim Erstellen der PDFs');
        } finally {
            setCreating(false);
            setCreatedCount(0);
        }
    };

    const handleDownloadAllPdfs = async () => {
        if (!pdfStatus || !pdfStatus.allPdfsCreated) {
            return;
        }

        setDownloading(true);

        try {
            const response = await fetch(`/api/rechnungen/project/${projectId}/download-all-pdfs`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Fehler beim Herunterladen der PDFs');
            }

            // Get filename from Content-Disposition header
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = 'project_pdfs.zip';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success('Alle PDFs wurden heruntergeladen');
        } catch (error) {
            console.error('Error downloading PDFs:', error);
            toast.error(error instanceof Error ? error.message : 'Fehler beim Herunterladen der PDFs');
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return (
            <div className="pdf-status-section">
                <div className="pdf-status-loading">Lade PDF-Status...</div>
            </div>
        );
    }

    if (!pdfStatus || pdfStatus.totalRechnungen === 0) {
        return null; // Don't show if there are no invoices
    }

    return (
        <div className="pdf-status-section">
            <div className="pdf-status-header">
                <h4>PDF-Status</h4>
            </div>
            <div className="pdf-status-content">
                <div className="pdf-status-info">
                    <span className="pdf-status-label">PDFs erstellt: </span>
                    <span className={`pdf-status-value ${creating ? 'creating' : ''}`}>
                        {creating ? (
                            <>
                                <span className="spinner"></span>
                                {pdfStatus.rechnungenWithPdf + createdCount} / {pdfStatus.totalRechnungen}
                            </>
                        ) : (
                            `${pdfStatus.rechnungenWithPdf} / ${pdfStatus.totalRechnungen}`
                        )}
                    </span>
                </div>

                <div className="pdf-status-actions">
                    {!pdfStatus.allPdfsCreated && pdfStatus.rechnungenWithoutPdf > 0 ? (
                        <button
                            className="pdf-action-btn create-btn"
                            onClick={handleCreateRemainingPdfs}
                            disabled={creating}
                        >
                            {creating ? (
                                <>
                                    <span className="spinner"></span>
                                    PDFs werden erstellt...
                                </>
                            ) : (
                                '📄 Fehlende PDFs erstellen'
                            )}
                        </button>
                    ) : pdfStatus.allPdfsCreated ? (
                        <button
                            className="download-file-btn"
                            onClick={handleDownloadAllPdfs}
                            disabled={downloading}
                        >
                            {downloading ? (
                                <>
                                    <span className="spinner"></span>
                                    Wird heruntergeladen...
                                </>
                            ) : (
                                '📦 Alle PDFs herunterladen'
                            )}
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default PdfStatusSection;
